import type { SupabaseClient } from '../../lib/supabase';
import type {
  ApproveReviewInput,
  OpenReviewInput,
  ReviewPerson,
  SamplePhoto,
  SampleSubmission,
  SupplierCandidate,
  SupplierReview,
  SupplierReviewComment,
  SupplierReviewStatus,
} from './types';

/**
 * Database access for the supplier review branch. Every permission rule
 * (who sees, opens, decides, submits) is decided by the database and proved
 * by the Phase 6 Stage 1 staging assertions; these functions only speak the
 * protocol. Identity names come exclusively through the two definer bridges
 * (review_people, supplier_users_for_org) — profiles rows stay hidden across
 * the TAP/supplier boundary.
 */

function fail(message: string): never {
  throw new Error(message);
}

// prettier-ignore
const REVIEW_COLUMNS = 'id, task_id, project_id, supplier_organization_id, shared_title, instructions, review_due_date, status_id, opened_by, approved_design_file_id, approved_sample_submission_id, approved_by, approved_at, approval_note, production_authorized, created_at, updated_at';
// prettier-ignore
const STATUS_COLUMNS = 'id, system_key, name, position, is_active, created_at, updated_at';
// prettier-ignore
const SUBMISSION_COLUMNS = 'id, review_id, version_number, note, is_submitted, submitted_at, created_by, created_at, updated_at';
// prettier-ignore
const PHOTO_COLUMNS = 'id, submission_id, review_id, label, name, drive_file_id, url, mime_type, size_bytes, uploaded_by, created_at';
// prettier-ignore
const COMMENT_COLUMNS = 'id, review_id, project_id, author_id, body, edited_at, created_at, updated_at';

export async function fetchReviewStatuses(client: SupabaseClient): Promise<SupplierReviewStatus[]> {
  const { data, error } = await client
    .from('supplier_review_statuses')
    .select(STATUS_COLUMNS)
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/** The task's review, or null when the branch was never opened. */
export async function fetchReviewForTask(
  client: SupabaseClient,
  taskId: string,
): Promise<SupplierReview | null> {
  const { data, error } = await client
    .from('supplier_reviews')
    .select(REVIEW_COLUMNS)
    .eq('task_id', taskId)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  return data;
}

/** Every review the caller may see (the supplier portal's working set). */
/**
 * The reviews the caller may see.
 *
 * `organizationId` narrows explicitly rather than leaving it to RLS. For a
 * real supplier the two agree — their policy returns their own org and
 * nothing else. For a super admin in Mimic Mode they do NOT:
 * is_active_supplier_of() matches every organization for them, so an
 * unfiltered read returns every factory's reviews at once and the "Viewing
 * as" picker changes nothing. Found by clicking it (Batch 7c smoke test).
 */
export async function fetchVisibleReviews(
  client: SupabaseClient,
  organizationId?: string,
): Promise<SupplierReview[]> {
  let query = client.from('supplier_reviews').select(REVIEW_COLUMNS);
  if (organizationId !== undefined && organizationId !== '') {
    query = query.eq('supplier_organization_id', organizationId);
  }
  const { data, error } = await query.order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Opens the branch (handoff 33): the review row first, then the selected
 * supplier users. A member failure after the review exists is surfaced —
 * the review stands, the caller re-offers member selection.
 */
export async function openSupplierReview(
  client: SupabaseClient,
  input: OpenReviewInput,
): Promise<SupplierReview> {
  const { data, error } = await client
    .from('supplier_reviews')
    .insert({
      task_id: input.taskId,
      project_id: input.projectId,
      supplier_organization_id: input.supplierOrganizationId,
      shared_title: input.sharedTitle.trim(),
      instructions: input.instructions.trim(),
      review_due_date: input.reviewDueDate,
      opened_by: input.openedBy,
    })
    .select(REVIEW_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }

  if (input.memberUserIds.length > 0) {
    const { error: memberError } = await client.from('supplier_review_members').insert(
      input.memberUserIds.map((userId) => ({
        review_id: data.id,
        supplier_organization_id: input.supplierOrganizationId,
        user_id: userId,
        added_by: input.openedBy,
      })),
    );
    if (memberError) {
      fail(memberError.message);
    }
  }
  return data;
}

/** Brief, due date and workflow status; approval moves through decisions. */
export async function updateReview(
  client: SupabaseClient,
  reviewId: string,
  changes: Partial<
    Pick<SupplierReview, 'shared_title' | 'instructions' | 'review_due_date' | 'status_id'>
  >,
): Promise<SupplierReview> {
  const { data, error } = await client
    .from('supplier_reviews')
    .update(changes)
    .eq('id', reviewId)
    .select(REVIEW_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('review_not_permitted');
  }
  return data;
}

/** The whole approval record in one statement (handoff 37). */
export async function approveReview(
  client: SupabaseClient,
  input: ApproveReviewInput,
): Promise<SupplierReview> {
  const { data, error } = await client
    .from('supplier_reviews')
    .update({
      approved_at: new Date().toISOString(),
      approved_by: input.approvedBy,
      approved_design_file_id: input.approvedDesignFileId,
      approved_sample_submission_id: input.approvedSampleSubmissionId,
      approval_note: input.approvalNote.trim() === '' ? null : input.approvalNote.trim(),
      production_authorized: true,
      status_id: input.approvedStatusId,
    })
    .eq('id', input.reviewId)
    .select(REVIEW_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('review_not_permitted');
  }
  return data;
}

/** The deliberate reopen (handoff 37): clears the whole record at once. */
export async function reopenReview(
  client: SupabaseClient,
  reviewId: string,
  nextStatusId: string,
): Promise<SupplierReview> {
  const { data, error } = await client
    .from('supplier_reviews')
    .update({
      approved_at: null,
      approved_by: null,
      approved_design_file_id: null,
      approved_sample_submission_id: null,
      approval_note: null,
      production_authorized: false,
      status_id: nextStatusId,
    })
    .eq('id', reviewId)
    .select(REVIEW_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('review_not_permitted');
  }
  return data;
}

export async function deleteReview(client: SupabaseClient, reviewId: string): Promise<void> {
  const { error } = await client.from('supplier_reviews').delete().eq('id', reviewId);
  if (error) {
    fail(error.message);
  }
}

// --------------------------------------------------------------------------
// Members and identity bridges
// --------------------------------------------------------------------------

export async function fetchReviewMembers(
  client: SupabaseClient,
  reviewId: string,
): Promise<{ id: string; user_id: string }[]> {
  const { data, error } = await client
    .from('supplier_review_members')
    .select('id, user_id')
    .eq('review_id', reviewId)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function addReviewMember(
  client: SupabaseClient,
  input: { reviewId: string; supplierOrganizationId: string; userId: string; addedBy: string },
): Promise<void> {
  const { error } = await client.from('supplier_review_members').insert({
    review_id: input.reviewId,
    supplier_organization_id: input.supplierOrganizationId,
    user_id: input.userId,
    added_by: input.addedBy,
  });
  if (error) {
    fail(error.message);
  }
}

export async function removeReviewMember(client: SupabaseClient, memberId: string): Promise<void> {
  const { error } = await client.from('supplier_review_members').delete().eq('id', memberId);
  if (error) {
    fail(error.message);
  }
}

/** The section 33 picker: active supplier users of one active organization. */
export async function fetchSupplierCandidates(
  client: SupabaseClient,
  organizationId: string,
): Promise<SupplierCandidate[]> {
  const { data, error } = await client.rpc('supplier_users_for_org', { p_org: organizationId });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/** Names for everyone a review's surfaces mention, via the definer bridge. */
export async function fetchReviewPeople(
  client: SupabaseClient,
  reviewId: string,
): Promise<ReviewPerson[]> {
  const { data, error } = await client.rpc('review_people', { p_review: reviewId });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Every file shared into reviews the caller participates in (the portal's
 * Files for Review tab). RLS already scopes rows; the flag narrows intent.
 */
export async function fetchSupplierVisibleFiles(
  client: SupabaseClient,
): Promise<
  { id: string; task_id: string | null; name: string; url: string; created_at: string }[]
> {
  const { data, error } = await client
    .from('task_files')
    .select('id, task_id, name, url, created_at')
    .eq('is_supplier_visible', true)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

// --------------------------------------------------------------------------
// Sample submissions (handoff 36)
// --------------------------------------------------------------------------

export async function fetchSubmissions(
  client: SupabaseClient,
  reviewId: string,
): Promise<SampleSubmission[]> {
  const { data, error } = await client
    .from('sample_submissions')
    .select(SUBMISSION_COLUMNS)
    .eq('review_id', reviewId)
    .order('version_number', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchSamplePhotos(
  client: SupabaseClient,
  reviewId: string,
): Promise<SamplePhoto[]> {
  const { data, error } = await client
    .from('sample_photos')
    .select(PHOTO_COLUMNS)
    .eq('review_id', reviewId)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/** A new draft version; the database owns the version number. */
export async function createSubmission(
  client: SupabaseClient,
  input: { reviewId: string; note: string; createdBy: string },
): Promise<SampleSubmission> {
  const { data, error } = await client
    .from('sample_submissions')
    .insert({ review_id: input.reviewId, note: input.note.trim(), created_by: input.createdBy })
    .select(SUBMISSION_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function addSamplePhoto(
  client: SupabaseClient,
  input: {
    submissionId: string;
    reviewId: string;
    label: string;
    name: string;
    driveFileId: string | null;
    url: string;
    mimeType: string | null;
    sizeBytes: number | null;
    uploadedBy: string;
  },
): Promise<SamplePhoto> {
  const { data, error } = await client
    .from('sample_photos')
    .insert({
      submission_id: input.submissionId,
      review_id: input.reviewId,
      label: input.label.trim(),
      name: input.name,
      drive_file_id: input.driveFileId,
      url: input.url,
      mime_type: input.mimeType,
      size_bytes: input.sizeBytes,
      uploaded_by: input.uploadedBy,
    })
    .select(PHOTO_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

/** The explicit Submit for TAP Review (handoff 36). One-way by design. */
export async function submitSubmission(
  client: SupabaseClient,
  submissionId: string,
): Promise<SampleSubmission> {
  const { data, error } = await client
    .from('sample_submissions')
    .update({ is_submitted: true })
    .eq('id', submissionId)
    .select(SUBMISSION_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('review_not_permitted');
  }
  return data;
}

// --------------------------------------------------------------------------
// The shared conversation (handoff 35)
// --------------------------------------------------------------------------

export async function fetchReviewComments(
  client: SupabaseClient,
  reviewId: string,
): Promise<SupplierReviewComment[]> {
  const { data, error } = await client
    .from('supplier_review_comments')
    .select(COMMENT_COLUMNS)
    .eq('review_id', reviewId)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function createReviewComment(
  client: SupabaseClient,
  input: { reviewId: string; projectId: string; authorId: string; body: string },
): Promise<SupplierReviewComment> {
  const { data, error } = await client
    .from('supplier_review_comments')
    .insert({
      review_id: input.reviewId,
      project_id: input.projectId,
      author_id: input.authorId,
      body: input.body.trim(),
    })
    .select(COMMENT_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function updateReviewCommentBody(
  client: SupabaseClient,
  commentId: string,
  body: string,
): Promise<SupplierReviewComment> {
  const { data, error } = await client
    .from('supplier_review_comments')
    .update({ body: body.trim() })
    .eq('id', commentId)
    .select(COMMENT_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  if (!data) {
    fail('review_not_permitted');
  }
  return data;
}

export async function deleteReviewComment(
  client: SupabaseClient,
  commentId: string,
): Promise<void> {
  const { error } = await client.from('supplier_review_comments').delete().eq('id', commentId);
  if (error) {
    fail(error.message);
  }
}
